import React from 'react'
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import { useTranslation } from 'react-i18next'
import { ProgramInviteFamilyOptionsResponsePrograms } from '@swagger'
import TextButton, { TextButtonVariant } from '../Buttons/TextButton'

export interface InviteRow {
  id: string
  programKey: number | null
  previousProgramType: string | null
  isApplicationFeePaid: boolean
  isProgramFull?: boolean
}

export type InviteRowUpdate =
  | { field: 'programKey'; value: number }
  | { field: 'isApplicationFeePaid'; value: boolean }

export interface StudentInviteRowProps {
  field: InviteRow
  programs: ProgramInviteFamilyOptionsResponsePrograms[]
  isSelectorGloballyDisabled: boolean
  formatProgramLabel: (
    program: ProgramInviteFamilyOptionsResponsePrograms
  ) => string
  isProgramDisabledForSelector: (
    program: ProgramInviteFamilyOptionsResponsePrograms,
    currentFieldProgramKey: number | null
  ) => boolean
  onUpdate: (fieldId: string, update: InviteRowUpdate) => void
  onRemove: (fieldId: string) => void
}

export const StudentInviteRow: React.FC<StudentInviteRowProps> = ({
  field,
  programs,
  isSelectorGloballyDisabled,
  formatProgramLabel,
  isProgramDisabledForSelector,
  onUpdate,
  onRemove,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const previousProgramSx = {
    marginRight: 2,
    width: '25%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginRight: 0,
      my: 1,
    },
  }

  return (
    <Box
      sx={{
        gridColumn: '1/ span 4',
        display: 'flex',
        [theme.breakpoints.down('sm')]: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: theme.spacing(0, 0),
        },
      }}
    >
      <PersonIcon
        sx={{
          color: theme.palette.textOrIcon.personIcon,
          height: '48px',
          width: '48px',
        }}
      />

      {field.previousProgramType ? (
        <TextField
          id={`previous-${field.id}`}
          label={t(
            'StudentInviteRow.FormField.PreviousProgram',
            'Previous Program'
          )}
          variant="filled"
          value={field.previousProgramType}
          disabled
          sx={previousProgramSx}
        />
      ) : (
        <Box sx={previousProgramSx} />
      )}

      <TextField
        id={`program-${field.id}`}
        label={t('StudentInviteRow.FormField.Program', 'Program')}
        variant="filled"
        select
        disabled={isSelectorGloballyDisabled}
        value={field.programKey ?? ''}
        SelectProps={{
          onChange: (e: SelectChangeEvent<unknown>) =>
            onUpdate(field.id, {
              field: 'programKey',
              value: Number(e.target.value),
            }),
        }}
        sx={{
          marginRight: 2,
          width: '25%',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
            marginRight: 0,
            my: 1,
          },
          '& .MuiFormHelperText-root': {
            '&.Mui-error': {
              color: theme.palette.error.main,
            },
          },
        }}
        error={field.isProgramFull}
        helperText={
          field.isProgramFull
            ? t('StudentInviteRow.FormField.ProgramFull', 'No Spots Available')
            : null
        }
      >
        {programs.map((program) => (
          <MenuItem
            key={program.programKey}
            value={program.programKey}
            disabled={isProgramDisabledForSelector(program, field.programKey)}
          >
            {formatProgramLabel(program)}
          </MenuItem>
        ))}
      </TextField>

      {field.programKey != null && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={field.isApplicationFeePaid}
                name="applicationFeePaid"
                disabled={field.isProgramFull}
                onChange={() =>
                  onUpdate(field.id, {
                    field: 'isApplicationFeePaid',
                    value: !field.isApplicationFeePaid,
                  })
                }
                sx={{
                  color: theme.palette.textOrIcon.checkbox,
                  padding: theme.spacing(0, 0, 0, 1),
                  [theme.breakpoints.down('sm')]: {
                    padding: theme.spacing(1, 0),
                  },
                }}
              />
            }
            label={
              <Typography variant="subtitle2" component="p">
                {t(
                  'StudentInviteRow.Checkbox.ApplicationFeeReceived',
                  'Application fee received from family'
                )}
              </Typography>
            }
          />
          <TextButton
            css={{ padding: theme.spacing(0, 0, 0, 0) }}
            id={`removeButton-${field.id}`}
            variant={TextButtonVariant.Remove}
            onClick={() => onRemove(field.id)}
          />
        </>
      )}
    </Box>
  )
}
